import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { Category } from './category';
import { ProductOptView, ProductOptViewProxy } from '../views/productopt';

import { PriceZone } from './pricezone';
import { PriceTime } from './pricetime';
import { OptionPrice } from './optionprice';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { ProductType } from '../types';
import { CategoryType } from '../types';
import { I18nValueType } from '../types';
import { OptionPriceType } from '../types';

const _schema = {
    "name": "PRODUCTOPT",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "product", type: "objid" },
        { name: "category", type: "objid" },
        { name: "name", type: "objid" },
        { name: "price", type: "number" },
        { name: "description", type: "objid" },
        { name: "sort", type: "number" },
        { name: "flags", type: "string" },
        { name: 'ctgref', type: "string", volatile: true }
    ],
    relate: [
        { direction: ">", target: "PRODUCT", by: "product", name: "product", reason: "product", child: false, class: ProductType },
        { direction: ">", target: "CATEGORY", by: "category", name: "category", reason: "category", child: false, class: CategoryType },
        { direction: ">", target: "I18NVALUE", by: "name", name: "name", reason: "name", child: true, class: I18nValueType },
        { direction: ">", target: "I18NVALUE", by: "description", name: "description", reason: "description", child: true, class: I18nValueType },
        { direction: "<", target: "OPTIONPRICE", by: "productopt", name: "price", reason: "prices", child: true, class: OptionPriceType }
    ]
} as const;

const _ProductOptClass = CreateObject(_schema);
if (!_ProductOptClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class ProductOpt extends _ProductOptClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): ProductOpt {
        return this._Copy(store) as ProductOpt;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'category' && related instanceof Category){
            if (related === this.category){
                related.AddOption(this);
            }
        }
    }

    override get viewProxy(): ProductOptView {
        return ProductOptViewProxy(this, this.data);
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;
        if (this.category){
            if ((this.status == 'DE') && !this.Commited){
                this.category.DelOption(this);
            }
            else {
                this.category.DoRefresh(this.table);
            }    
        }
    }

    /****************************/
    /* CATALOG CONSIDERATIONS   */
    /****************************/

    override get IsCatalog(): boolean {
        return super.IsCatalog;
    }

    override set IsCatalog(value: boolean){
        if (value == this.IsCatalog){
            return;
        }

        super.IsCatalog = value;
        if (value == false){
            if (this.status == 'CT'){
                this.status = 'AC';
            }

            for (const _child of this.Children){
                _child.IsCatalog = value;
            }
        }
    }
        
    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get available(): boolean {
        return this.status == 'AC';
    }

    set available(value: boolean){
        this.status = (value) ? 'AC' : 'UN';
    }

    get IsValid(): boolean {
        return !!this.status && ((this.status == 'AC') || (this.status == 'UN'));
    }

    get IsAvailable(): boolean {
        return this.available;
    }

    /****************************/
    /* ZONE/PERIOD PRICE        */
    /****************************/

    /**
     * Applicable price for `zone` + `time`: exact override when present, else the custom
     * zone default (`time == null`) when asking for a time slot on a custom zone, else
     * the base `price` field. Both `null` is the base slot (never an `OPTIONPRICE` row).
     */
    GetPriceAt(zone: PriceZone | null, time: PriceTime | null): number | null {
        if (zone === null && time === null){
            return this.price ?? null;
        }

        for (const _row of this.prices){
            if (_row.IsValid && (_row.pricezone === zone) && (_row.pricetime === time)){
                const _exact = _row.price ?? null;
                if (_exact !== null){
                    return _exact;
                }
                break;
            }
        }

        if (zone !== null && time !== null){
            for (const _row of this.prices){
                if (_row.IsValid && (_row.pricezone === zone) && (_row.pricetime === null)){
                    const _zoneDefault = _row.price ?? null;
                    if (_zoneDefault !== null){
                        return _zoneDefault;
                    }
                    break;
                }
            }
        }

        return this.price ?? null;
    }

    /**
     * Writes the price for `zone` + `time`. Both `null` writes the base `price` field
     * directly (ignored when `price` is `null`, since the base price cannot be cleared this
     * way); otherwise creates, updates or clears the `OPTIONPRICE` override row for that slot,
     * without duplicating rows for the same slot (`price === null` deletes the row if present).
     */
    SetPriceAt(zone: PriceZone | null, time: PriceTime | null, price: number | null): void {
        if (zone === null && time === null){
            if (price !== null){
                this.price = price;
            }
            return;
        }

        let _existing: OptionPrice | null = null;
        for (const _row of this.prices){
            if (_row.pricezone === zone && _row.pricetime === time){
                _existing = _row;
                break;
            }
        }

        if (price === null){
            if (_existing){
                _existing.status = 'DE';
            }
            return;
        }

        if (_existing){
            _existing.price = price;
            if (!_existing.IsValid){
                _existing.status = 'AC';
            }
            _existing.ToUpdate = true;
            return;
        }

        const _row = new OptionPrice(null, this.data);
        _row.productopt = this;
        _row.pricezone = zone;
        _row.pricetime = time;
        _row.price = price;
        _row.status = 'AC';
        this.AddPrice(_row);
    }
}

